import { supabase } from './supabase';
import { fetchAllRows } from './fetch-all-rows';
import { toBaliDateKey } from './date-utils';
import type { PostgrestError } from '@supabase/supabase-js';

/**
 * Refund totals, fetched once and keyed every way the reports need.
 *
 * The reporting decision for refunds is a **contra line, not silent netting**:
 * `memberships.price` stays gross everywhere, and Finance and Memberships show
 * refunds as their own "less refunds" figure. Netting is applied only to
 * per-member numbers, where leaving a refunded member counted as a payer is a
 * factual error rather than a rounding one.
 *
 * Two keys, deliberately:
 *
 *  - `byMember` uses `refunded_to_member_id`, the snapshot of who was actually
 *    paid. `transfer_membership()` reassigns `memberships.member_id` in place,
 *    so keying per-member figures off the membership's *current* owner would
 *    net a refund against whoever holds it now rather than whoever received
 *    the money.
 *  - `byMembership` is for predicates that ask "was this membership paid for",
 *    which is a per-row question.
 *
 * Voided refunds are excluded — they are struck from the ledger and must not
 * reduce anything.
 */

export interface RefundTotals {
  /** refunded_to_member_id -> gross refunded (all methods). */
  byMember: Map<string, number>;
  /** membership_id -> gross refunded (all methods). */
  byMembership: Map<string, number>;
  /**
   * Bali date key -> CASH refunded that day. Gift-card credit is excluded: no
   * money left the bank, so it must never reduce a cash-flow series.
   */
  cashByDate: Map<string, number>;
  /** Gross refunded overall, all methods. */
  total: number;
  /** Gross refunded overall, cash only. */
  cashTotal: number;
}

export const EMPTY_REFUND_TOTALS: RefundTotals = {
  byMember: new Map(),
  byMembership: new Map(),
  cashByDate: new Map(),
  total: 0,
  cashTotal: 0,
};

interface RefundRow {
  membership_id: string;
  refunded_to_member_id: string;
  amount_gross: number | string;
  is_cash: boolean;
  refunded_on: string;
}

const add = (map: Map<string, number>, key: string | null, amount: number) => {
  if (!key) return;
  map.set(key, (map.get(key) ?? 0) + amount);
};

/**
 * Fetch every live refund and index it.
 *
 * Paged: an unbounded select would silently stop at PostgREST's 1,000-row cap,
 * and a truncated refund total understates refunds — the worst direction for a
 * figure whose whole job is to reduce revenue.
 */
let inFlight: Promise<{ data: RefundTotals; error: PostgrestError | null }> | null = null;

/**
 * Single-flight wrapper. A Finance or Members page mounts several report
 * surfaces at once and each needs these totals; without this they each page
 * through `membership_refunds` separately. Concurrent callers share one read.
 *
 * Deliberately NOT a cache with a TTL: the promise is released as soon as it
 * settles, so the next call re-fetches. A refund recorded a moment ago can
 * never be served stale — which matters more here than saving a query.
 */
export function fetchRefundTotals(): Promise<{
  data: RefundTotals;
  error: PostgrestError | null;
}> {
  if (inFlight) return inFlight;
  inFlight = readRefundTotals().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function readRefundTotals(): Promise<{
  data: RefundTotals;
  error: PostgrestError | null;
}> {
  const { data, error } = await fetchAllRows<RefundRow>(() =>
    supabase
      .from('membership_refunds')
      .select('membership_id, refunded_to_member_id, amount_gross, is_cash, refunded_on')
      .is('voided_at', null),
  );

  if (error) return { data: EMPTY_REFUND_TOTALS, error };

  const totals: RefundTotals = {
    byMember: new Map(),
    byMembership: new Map(),
    cashByDate: new Map(),
    total: 0,
    cashTotal: 0,
  };

  for (const row of data || []) {
    const amount = Number(row.amount_gross) || 0;
    if (amount <= 0) continue;

    add(totals.byMember, row.refunded_to_member_id, amount);
    add(totals.byMembership, row.membership_id, amount);
    totals.total += amount;

    if (row.is_cash) {
      totals.cashTotal += amount;
      // Bucketed on refunded_on, never the sale date: that is how the cash
      // actually moved, and it keeps a closed month from being restated.
      add(totals.cashByDate, toBaliDateKey(row.refunded_on), amount);
    }
  }

  return { data: totals, error: null };
}

/**
 * What the club kept on ONE membership, after any refund against it.
 *
 * Net per membership row, never per member against an aggregate. Every revenue
 * surface here filters `status = 'valid'`, so a cancelled membership is already
 * excluded from the sum — and most refunds accompany a cancellation. Subtracting
 * that refund from the member's total as well removes it twice, and the second
 * removal eats into the member's OTHER memberships: someone who was refunded a
 * Rp 46.2M membership last year and holds a Rp 20M one today would read as
 * having spent nothing.
 *
 * Netting row by row keeps the deduction attached to the money it reverses.
 */
export function netMembershipPrice(
  membershipId: string,
  price: number | string | null | undefined,
  totals: RefundTotals,
): number {
  const gross = Number(price) || 0;
  if (gross <= 0) return 0;
  return Math.max(gross - (totals.byMembership.get(membershipId) ?? 0), 0);
}

/**
 * Did this membership leave any money with the club?
 *
 * Replaces a bare `price > 0` test wherever that stands in for "this member
 * paid": a membership refunded in full was paid for and then wasn't, and
 * counting it still makes a refunded member look like a paying one.
 */
export function membershipStillPaid(
  membershipId: string,
  price: number | string | null | undefined,
  totals: RefundTotals,
): boolean {
  const gross = Number(price) || 0;
  if (gross <= 0) return false;
  return gross - (totals.byMembership.get(membershipId) ?? 0) > 0;
}
